import type { FormativeHomeCard } from '../../tutor/api/malayFormativeProgress.client';
import type { TutorAnalyticsSummary } from '../../../tutor/types/tutorAnalytics';
import { resolveCardStatusKey } from '../../tutor/malayFormativeHomeCards';
import { isSameLocalDay, localIsoDate, parseTimestamp } from './dashboardDateUtils';

export type MalayTodaysLearningSnapshot = {
  tutorMinutesToday: number | null;
  tutorSessionsToday: number | null;
  activitiesInProgress: number;
  activitiesCompleted: number;
  hasAnyData: boolean;
};

function latestActivityIso(card: FormativeHomeCard): string | null | undefined {
  const progressAt = card.progress?.last_activity_at;
  const savedAt = card.narrative_writing?.last_saved_at;
  if (progressAt && savedAt) {
    const p = parseTimestamp(progressAt);
    const s = parseTimestamp(savedAt);
    if (p && s) return p.getTime() >= s.getTime() ? progressAt : savedAt;
    return progressAt || savedAt;
  }
  return progressAt ?? savedAt ?? null;
}

/** Summarise today's real learning signals — never invents goals or lifetime-as-today counts. */
export function buildMalayTodaysLearningSnapshot(params: {
  formativeCards: FormativeHomeCard[];
  tutorAnalytics: TutorAnalyticsSummary | null;
  now?: Date;
}): MalayTodaysLearningSnapshot {
  const now = params.now ?? new Date();
  const today = localIsoDate(now);
  const todayEngagement = params.tutorAnalytics?.daily_engagement?.find(
    (row) => row.date === today,
  );

  let activitiesInProgress = 0;
  let activitiesCompleted = 0;
  for (const card of params.formativeCards) {
    const latestIso = latestActivityIso(card);
    if (!isSameLocalDay(latestIso, now)) continue;
    const status = resolveCardStatusKey(card);
    if (status === 'completed') activitiesCompleted += 1;
    else if (status === 'inProgress') activitiesInProgress += 1;
  }

  const tutorMinutesToday =
    todayEngagement && todayEngagement.tutor_minutes > 0
      ? Math.round(todayEngagement.tutor_minutes * 10) / 10
      : null;
  const tutorSessionsToday =
    todayEngagement && todayEngagement.sessions > 0 ? todayEngagement.sessions : null;

  const hasAnyData =
    activitiesInProgress > 0 ||
    activitiesCompleted > 0 ||
    tutorMinutesToday != null ||
    tutorSessionsToday != null;

  return {
    tutorMinutesToday,
    tutorSessionsToday,
    activitiesInProgress,
    activitiesCompleted,
    hasAnyData,
  };
}
